import {
  LightweightElement,
  LightweightElementsRecord,
} from "@/store/lightweight-elements-slice";
import {
  IElement,
  IElementType,
  IElementTypeHint,
} from "@faro-lotv/ielement-types";
import { IElementsRecord } from "@faro-lotv/project-source";

/**
 * Calculate the custom date for a dataset by finding the latest img360 or path element
 *
 * @param dataset The dataset element to calculate the custom date for
 * @param elements All elements in the project
 * @returns The creation date of the latest img360 or path element, or undefined if none found
 */
export function findLatestReferenceDate(
  dataset: LightweightElement | IElement,
  elements: LightweightElementsRecord | IElementsRecord,
): string | undefined {
  const dateElements: Array<LightweightElement | IElement> = [];

  function collectElements(
    node: LightweightElement | IElement,
    elements: LightweightElementsRecord | IElementsRecord,
  ): void {
    // When fetching lightweight elements at project start, the img360 of the path are not available
    // to avoid using a wrong date, the odometryPath themselves are considered, since it's safe to assume
    // they are created at the same time as their img360 children
    if (
      node.type === IElementType.img360 ||
      node.typeHint === IElementTypeHint.odometryPath
    ) {
      dateElements.push(node);
    }
    for (const childId of node.childrenIds ?? []) {
      const child = elements[childId];
      if (child) {
        collectElements(child, elements);
      }
    }
  }

  collectElements(dataset, elements);

  if (dateElements.length === 0) return;

  // Find the element with the latest createdAt
  const latestElement = dateElements.reduce((latest, current) =>
    Date.parse(current.createdAt) > Date.parse(latest.createdAt)
      ? current
      : latest,
  );

  return latestElement.createdAt;
}

/**
 * CustomDatesCache class that manages a map of element IDs to their custom dates
 */
export class CustomDatesCache {
  public customDates: Map<string, string>;

  constructor() {
    this.customDates = new Map<string, string>();
  }

  /**
   * Find and cache the latest img360 date for a dataset element
   *
   * @param datasetId The ID of the dataset element
   * @param elements All elements in the project
   * @returns The creation date of the latest img360 element, or undefined if none found
   */
  findCustomDateForDataset(
    datasetId: string,
    elements: LightweightElementsRecord | IElementsRecord,
  ): string | undefined {
    // Check cache first
    const cached = this.customDates.get(datasetId);
    if (cached) return cached;

    const dataset = elements[datasetId];
    if (!dataset) return;

    const customDate = findLatestReferenceDate(dataset, elements);
    if (customDate) {
      this.customDates.set(datasetId, customDate);
    }
    return customDate;
  }
}
